/**
 * FieldMapper - Visual field mapping component
 * 
 * Pattern source: Tim-Combo's VisualFieldMapper
 * Steal patterns: Drag connections, auto-suggestions, confidence scores
 */

import React, { useState } from 'react';

interface Field {
  name: string;
  type: 'string' | 'number' | 'date' | 'boolean' | 'object' | 'array';
  description?: string;
  required?: boolean;
}

interface FieldMapping {
  sourceField: string;
  targetField: string;
  transformation: {
    type: 'direct' | 'computed' | 'lookup' | 'conditional';
    expression?: string;
    lookupTable?: string;
    condition?: string;
  };
  confidence?: number;  // 0-10 scale (from auto-suggestion)
}

interface FieldMapperProps {
  sourceFields: Field[];
  targetFields: Field[];
  mappings: FieldMapping[];
  onMappingCreate: (mapping: FieldMapping) => void;
  onMappingUpdate: (mapping: FieldMapping) => void;
  onMappingDelete: (sourceField: string, targetField: string) => void;
}

export const FieldMapper: React.FC<FieldMapperProps> = ({
  sourceFields,
  targetFields,
  mappings,
  onMappingCreate,
  onMappingUpdate,
  onMappingDelete
}) => {
  const [draggedField, setDraggedField] = useState<Field | null>(null);
  const [selectedMapping, setSelectedMapping] = useState<FieldMapping | null>(null);

  // Auto-suggest mappings based on field names
  const suggestMapping = (sourceField: Field, targetField: Field): number => {
    // Exact match
    if (sourceField.name.toLowerCase() === targetField.name.toLowerCase()) {
      return 10;
    }

    // Similar names (levenshtein distance or simple contains)
    const sourceName = sourceField.name.toLowerCase();
    const targetName = targetField.name.toLowerCase();

    if (sourceName.includes(targetName) || targetName.includes(sourceName)) {
      return 8;
    }

    // Same type
    if (sourceField.type === targetField.type) {
      return 5;
    }

    return 0;
  };

  // Auto-suggest all mappings
  const autoSuggest = () => {
    const suggestions: FieldMapping[] = [];

    sourceFields.forEach(sourceField => {
      let bestMatchField: Field | null = null;
      let bestMatchConfidence = 0;

      targetFields.forEach(targetField => {
        const confidence = suggestMapping(sourceField, targetField);
        if (confidence > 7 && confidence > bestMatchConfidence) {
          bestMatchField = targetField;
          bestMatchConfidence = confidence;
        }
      });

      if (bestMatchField !== null) {
        const fieldName = (bestMatchField as Field).name;
        suggestions.push({
          sourceField: sourceField.name,
          targetField: fieldName,
          transformation: { type: 'direct' },
          confidence: bestMatchConfidence
        });
      }
    });

    suggestions.forEach(onMappingCreate);
  };

  // Handle field drop
  const handleFieldDrop = (targetField: Field) => {
    if (!draggedField) return;

    const confidence = suggestMapping(draggedField, targetField);

    const newMapping: FieldMapping = {
      sourceField: draggedField.name,
      targetField: targetField.name,
      transformation: { type: 'direct' },
      confidence
    };

    onMappingCreate(newMapping);
    setDraggedField(null);
  };

  return (
    <div className="field-mapper">
      {/* Header */}
      <div className="mapper-header">
        <h3>Field Mapping</h3>
        <button onClick={autoSuggest} className="btn-suggest">
          Auto-Suggest Mappings
        </button>
      </div>

      {/* Mapping grid */}
      <div className="mapping-grid">
        {/* Source fields */}
        <div className="field-column">
          <h4>Source Fields</h4>
          {sourceFields.map(field => (
            <div
              key={field.name}
              className="field-item"
              draggable
              onDragStart={() => setDraggedField(field)}
              onDragEnd={() => setDraggedField(null)}
            >
              <div className="field-name">{field.name}</div>
              <div className="field-type">{field.type}</div>
              {field.required && <span className="field-required">*</span>}
            </div>
          ))}
        </div>

        {/* Mappings visualization */}
        <div className="mapping-connections">
          <svg width="200" height="100%">
            {mappings.map((mapping, idx) => {
              const sourceIdx = sourceFields.findIndex(f => f.name === mapping.sourceField);
              const targetIdx = targetFields.findIndex(f => f.name === mapping.targetField);

              if (sourceIdx === -1 || targetIdx === -1) return null;

              const y1 = sourceIdx * 60 + 30;
              const y2 = targetIdx * 60 + 30;

              // Color based on confidence
              const confidence = mapping.confidence || 5;
              const color = confidence >= 8 ? '#10b981' : confidence >= 5 ? '#f59e0b' : '#6b7280';

              return (
                <g key={`${mapping.sourceField}-${mapping.targetField}`}>
                  <path
                    d={`M 0 ${y1} C 100 ${y1}, 100 ${y2}, 200 ${y2}`}
                    stroke={color}
                    strokeWidth="2"
                    fill="none"
                    onClick={() => setSelectedMapping(mapping)}
                    style={{ cursor: 'pointer' }}
                  />
                  {/* Confidence indicator */}
                  <circle cx="100" cy={(y1 + y2) / 2} r="8" fill={color} />
                  <text
                    x="100"
                    y={(y1 + y2) / 2 + 4}
                    textAnchor="middle"
                    fill="white"
                    fontSize="10"
                  >
                    {confidence}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>

        {/* Target fields */}
        <div className="field-column">
          <h4>Target Fields</h4>
          {targetFields.map(field => (
            <div
              key={field.name}
              className="field-item"
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleFieldDrop(field)}
            >
              <div className="field-name">{field.name}</div>
              <div className="field-type">{field.type}</div>
              {field.required && <span className="field-required">*</span>}
            </div>
          ))}
        </div>
      </div>

      {/* Mapping details panel */}
      {selectedMapping && (
        <div className="mapping-details">
          <h4>Mapping Configuration</h4>
          <div className="mapping-field">
            <label>Source Field:</label>
            <span>{selectedMapping.sourceField}</span>
          </div>
          <div className="mapping-field">
            <label>Target Field:</label>
            <span>{selectedMapping.targetField}</span>
          </div>
          <div className="mapping-field">
            <label>Transformation Type:</label>
            <select
              value={selectedMapping.transformation.type}
              onChange={(e) => {
                const updatedMapping = {
                  ...selectedMapping,
                  transformation: {
                    ...selectedMapping.transformation,
                    type: e.target.value as any
                  }
                };
                onMappingUpdate(updatedMapping);
                setSelectedMapping(updatedMapping);
              }}
            >
              <option value="direct">Direct (copy value)</option>
              <option value="computed">Computed (expression)</option>
              <option value="lookup">Lookup (table)</option>
              <option value="conditional">Conditional (if/else)</option>
            </select>
          </div>

          {/* Transformation-specific inputs */}
          {selectedMapping.transformation.type === 'computed' && (
            <div className="mapping-field">
              <label>Expression:</label>
              <input
                type="text"
                placeholder="e.g., value * 1.15"
                value={selectedMapping.transformation.expression || ''}
                onChange={(e) => {
                  const updatedMapping = {
                    ...selectedMapping,
                    transformation: {
                      ...selectedMapping.transformation,
                      expression: e.target.value
                    }
                  };
                  onMappingUpdate(updatedMapping);
                  setSelectedMapping(updatedMapping);
                }}
              />
            </div>
          )}

          {selectedMapping.transformation.type === 'lookup' && (
            <div className="mapping-field">
              <label>Lookup Table:</label>
              <input
                type="text"
                placeholder="e.g., employees"
                value={selectedMapping.transformation.lookupTable || ''}
                onChange={(e) => {
                  const updatedMapping = {
                    ...selectedMapping,
                    transformation: {
                      ...selectedMapping.transformation,
                      lookupTable: e.target.value
                    }
                  };
                  onMappingUpdate(updatedMapping);
                  setSelectedMapping(updatedMapping);
                }}
              />
            </div>
          )}

          {selectedMapping.transformation.type === 'conditional' && (
            <div className="mapping-field">
              <label>Condition:</label>
              <input
                type="text"
                placeholder="e.g., value > 100 ? 'high' : 'low'"
                value={selectedMapping.transformation.condition || ''}
                onChange={(e) => {
                  const updatedMapping = {
                    ...selectedMapping,
                    transformation: {
                      ...selectedMapping.transformation,
                      condition: e.target.value
                    }
                  };
                  onMappingUpdate(updatedMapping);
                  setSelectedMapping(updatedMapping);
                }}
              />
            </div>
          )}

          <div className="mapping-actions">
            <button
              onClick={() => {
                onMappingDelete(selectedMapping.sourceField, selectedMapping.targetField);
                setSelectedMapping(null);
              }}
              className="btn-danger"
            >
              Delete Mapping
            </button>
            <button onClick={() => setSelectedMapping(null)} className="btn-secondary">
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
